
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { AppSidebar } from "@/components/AppSidebar";
import { PageHeader } from "@/components/PageHeader";
import { DevicesSection } from "@/components/DevicesSection";
import { AppsSection } from "@/components/AppsSection";
import { CategoriesTagsSection } from "@/components/CategoriesTagsSection";
import { UsersSection } from "@/components/UsersSection";
import { AdminsSection } from "@/components/AdminsSection";
import { LevelsSection } from "@/components/LevelsSection";
import { NetworkRulesSection } from "@/components/NetworkRulesSection";
import { AddRuleSheet } from "@/components/AddRuleSheet";
import { PresetsSection } from "@/components/PresetsSection";
import { CategoriesSection } from "@/components/CategoriesSection";
import { BrowserGroupsSection } from "@/components/BrowserGroupsSection";
import { RestrictionsSection } from "@/components/RestrictionsSection";
import { BlockPageSection } from "@/components/BlockPageSection";
import { RequestsSection } from "@/components/RequestsSection";
import { ThemeConfiguratorSection } from "@/components/ThemeConfiguratorSection";
import { BrowserResultSection } from "@/components/BrowserResultSection";
import { VerifiedSection } from "@/components/VerifiedSection";
import { BrowserAnalyticsSection } from "@/components/BrowserAnalyticsSection";
import { BrowserSettingsSection } from "@/components/BrowserSettingsSection";

import { SidebarProvider, SidebarInset, SidebarTrigger } from "@/components/ui/sidebar";
import { useAutoVersionIncrement } from "@/hooks/useAutoVersionIncrement";
import { useIsMobile } from "@/hooks/use-mobile";

const VALID_SECTIONS = [
  "devices", "apps", "categories", "users", "levels",
  "network-rules", "browser-groups", "policies",
  "admins", "restrictions", "app-categories", "block-page", "requests", "theme", "result", "verify-apps", "browser-analytics", "browser-settings"
];

const SECTION_TITLES = {
  "devices": "Devices",
  "apps": "App Catalog",
  "categories": "Categories",
  "app-categories": "App Categories",
  "users": "Users",
  "admins": "Admins",
  "levels": "Levels",
  "network-rules": "Network Rules",
  "browser-groups": "Groups",
  "policies": "Policies",
  "restrictions": "Restrictions",
  "block-page": "Block Page Design",
  "requests": "Access Requests",
  "theme": "Theme Configurator",
  "result": "Result",
  "verify-apps": "Verified",
  "browser-analytics": "Analytics",
  "browser-settings": "Browser Settings"
};

const Index = () => {
  const { sectionId } = useParams();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [isAddRuleSheetOpen, setIsAddRuleSheetOpen] = useState(false);
  const [searchParams] = useSearchParams();

  // Auto-increment version when the app loads/changes
  useAutoVersionIncrement();
  
  // Determine the current active section
  const activeSection = sectionId && VALID_SECTIONS.includes(sectionId) ? sectionId : "devices";
  const hasDeviceSelected = activeSection === "devices" && !!searchParams.get('deviceId');
  const contentPaddingCls = "pl-0 pr-0";

  // Redirect to correct URL if needed
  useEffect(() => {
    if (!sectionId || !VALID_SECTIONS.includes(sectionId)) {
      navigate("/section/devices", { replace: true });
    }
  }, [sectionId, navigate]);

  const handleSectionChange = (section: string) => {
    navigate(`/section/${section}`);
  };

  const handleAddDevice = () => {
    // TODO: Implement add device functionality
    console.log("Add device clicked");
  };

  const handleAddRule = () => {
    setIsAddRuleSheetOpen(true);
  };

  const renderActiveSection = () => {
    switch (activeSection) {
      case "devices":
        return <DevicesSection />;
      case "apps":
        return <AppsSection />;
      case "categories":
        return <CategoriesSection />;
      case "app-categories":
        return <CategoriesTagsSection />;
      case "users":
        return <UsersSection />;
      case "admins":
        return <AdminsSection />;
      case "levels":
        return <LevelsSection />;
      case "network-rules":
        return <NetworkRulesSection onAddClick={handleAddRule} />;
      case "browser-groups":
        return <BrowserGroupsSection />;
      case "policies":
        return <PresetsSection />;
      case "restrictions":
        return <RestrictionsSection />;
      case "block-page":
        return <BlockPageSection />;
      case "requests":
        return <RequestsSection />;
      case "theme":
        return <ThemeConfiguratorSection />;
      case "result":
        return <BrowserResultSection />;
      case "verify-apps":
        return <VerifiedSection />;
      case "browser-analytics":
        return <BrowserAnalyticsSection />;
      case "browser-settings":
        return <BrowserSettingsSection />;
      default:
        return <DevicesSection />;
    }
  };

  const getHeaderProps = () => {
    const baseProps = {
      title: SECTION_TITLES[activeSection as keyof typeof SECTION_TITLES] || "Dashboard"
    };

    if (activeSection === "apps") {
      return {
        ...baseProps,
        hideTooltip: true
      };
    }

    if (activeSection === "devices") {
      return {
        ...baseProps,
        hideTooltip: true
      };
    }

    if (activeSection === "levels") {
      return {
        ...baseProps,
        tooltipText: "Create and manage device levels with custom rules"
      };
    }

    if (activeSection === "categories") {
      return {
        ...baseProps,
        tooltipText: "Manage website categories for the browser with hierarchical organization"
      };
    }

    if (activeSection === "network-rules") {
      return {
        ...baseProps,
        hideTooltip: true
      };
    }

    if (activeSection === "browser-groups") {
      return {
        ...baseProps,
        tooltipText: "Manage browser configuration groups"
      };
    }

    if (activeSection === "policies") {
      return {
        ...baseProps,
        tooltipText: "Create and manage browser policies with custom rules"
      };
    }

    if (activeSection === "block-page") {
      return {
        ...baseProps,
        tooltipText: "Create and manage custom block pages for filtered content"
      };
    }

    if (activeSection === "requests") {
      return {
        ...baseProps,
        tooltipText: "Review and manage user access requests"
      };
    }

    return baseProps;
  };


  return (
    <SidebarProvider defaultOpen={!isMobile} className="sidebar-provider">
      <div className="main-wrapper">
        <AppSidebar activeSection={activeSection} onSectionChange={handleSectionChange} />
        <SidebarInset className="flex-1 flex flex-col bg-transparent">
          {/* Mobile header with hamburger menu */}
          <div className="sticky top-0 z-50 bg-white border-b border-gray-200 pl-6 pr-6 py-3 flex items-center justify-between md:hidden">
            <h1 className="text-lg font-semibold text-gray-900">
              {SECTION_TITLES[activeSection as keyof typeof SECTION_TITLES] || "Dashboard"}
            </h1>
            <div className="flex items-center gap-2">
              {/* Add button for mobile - only show for network-rules section */}
               {activeSection === "network-rules" && (
                <button 
                  onClick={handleAddRule}
                  className="bg-primary text-primary-foreground hover:bg-primary/90 inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 h-8 px-3 text-xs"
                >
                  Add
                </button>
              )}
              <SidebarTrigger className="w-8 h-8 p-1 border border-gray-300 hover:bg-gray-100" />
            </div>
          </div>
          
          {/* Desktop header - hidden on mobile */}
          <div className={`hidden md:block ${contentPaddingCls}`}>
            <PageHeader {...getHeaderProps()} />
          </div>
          
          <main className={`flex-1 ${contentPaddingCls} pb-6 px-6`}>
            {renderActiveSection()}
          </main>
        </SidebarInset>

        {/* Add Rule Sheet */}
        <AddRuleSheet 
          isOpen={isAddRuleSheetOpen} 
          onClose={() => setIsAddRuleSheetOpen(false)} 
        />
      </div>
    </SidebarProvider>
  );
};

export default Index;
